export type TransactionType = 'income' | 'expense';

export interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  date: string; // ISO date string
  category: string;
  description: string;
  taxDeductible: boolean;
  superContribution?: number;
  accountId?: string; // links to Account.id
  createdAt: string;
}

export type AccountType = 'cash' | 'savings' | 'transaction' | 'credit_card' | 'other';

export interface Account {
  id: string;
  name: string;
  type: AccountType;
  initialBalance: number;
  createdAt: string;
}

export interface Transfer {
  id: string;
  fromAccountId: string;
  toAccountId: string;
  amount: number;
  date: string;
  description: string;
  createdAt: string;
}

export interface Budget {
  id: string;
  category: string;
  monthlyLimit: number;
  month: string; // YYYY-MM
}

export type BillFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly';

export interface Bill {
  id: string;
  name: string;
  amount: number;
  dueDate: string; // ISO date string
  frequency: BillFrequency;
  paid: boolean;
  lastPaidDate?: string;
}

export type ContributionFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'none';

export interface SavingsGoal {
  id: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  targetDate: string; // ISO date string
  contributionAmount?: number;
  contributionFrequency?: ContributionFrequency;
  lastContributionDate?: string; // ISO date string
  createdAt: string;
}

export interface Asset {
  id: string;
  name: string;
  description: string;
  purchaseDate: string;
  purchasePrice: number;
  currentValue: number;
  photo?: string; // base64 data URI
  room: string;
  insurerName?: string;
  policyNumber?: string;
  createdAt: string;
}

export type VehicleExpenseType = 'fuel' | 'rego' | 'insurance' | 'servicing' | 'tolls' | 'parking' | 'other';

export interface Vehicle {
  id: string;
  name: string; // e.g. "2020 Toyota Corolla"
  rego?: string;
  workUsePercent: number; // 0-100
  createdAt: string;
}

export interface VehicleExpense {
  id: string;
  vehicleId: string;
  type: VehicleExpenseType;
  amount: number;
  date: string;
  description: string;
  odometer?: number;
  createdAt: string;
}

export type ChallengeType = '52week' | 'roundup' | 'nospend';

export interface SavingsChallenge {
  id: string;
  type: ChallengeType;
  name: string;
  startDate: string; // ISO date
  completedWeeks?: boolean[]; // 52-week: which weeks are done
  roundupTotal?: number; // round-up sim running total
  noSpendDays?: string[]; // ISO date strings of no-spend days
  noSpendTarget?: number; // target days per month
  active: boolean;
  createdAt: string;
}

export interface AppData {
  transactions: Transaction[];
  budgets: Budget[];
  bills: Bill[];
  assets: Asset[];
  accounts: Account[];
  transfers: Transfer[];
  savingsGoals: SavingsGoal[];
  vehicles: Vehicle[];
  vehicleExpenses: VehicleExpense[];
  savingsChallenges: SavingsChallenge[];
  categories: string[];
  settings: AppSettings;
}

export interface PaydayConfig {
  mode: 'auto' | 'manual';
  frequency?: PayFrequency;
  nextDate?: string; // ISO date string
  amount?: number;
}

export type PayFrequency = 'weekly' | 'fortnightly' | 'monthly';

export interface AppSettings {
  autoLockMinutes: number;
  darkMode: boolean;
  paydayConfig?: PaydayConfig;
  achievementsResetAt?: string; // ISO date — ignore data before this for achievements
}

export const DEFAULT_CATEGORIES = [
  'Groceries',
  'Rent',
  'Mortgage',
  'Utilities',
  'Transport',
  'Fuel',
  'Insurance',
  'Medicare',
  'Health',
  'Entertainment',
  'Dining Out',
  'Clothing',
  'Education',
  'Childcare',
  'Subscriptions',
  'Phone & Internet',
  'Gifts',
  'Donations',
  'Travel',
  'Home Maintenance',
  'Personal Care',
  'Salary',
  'Freelance',
  'Investment',
  'Government Payment',
  'Other',
];

export const DEFAULT_ROOMS = [
  'Living Room',
  'Kitchen',
  'Bedroom',
  'Bathroom',
  'Garage',
  'Office',
  'Laundry',
  'Outdoor',
  'Other',
];

export const DEFAULT_APP_DATA: AppData = {
  transactions: [],
  budgets: [],
  bills: [],
  assets: [],
  accounts: [],
  transfers: [],
  savingsGoals: [],
  vehicles: [],
  vehicleExpenses: [],
  savingsChallenges: [],
  categories: DEFAULT_CATEGORIES,
  settings: {
    autoLockMinutes: 5,
    darkMode: false,
  },
};
